import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Flex, Tooltip, Typography } from 'antd';
import moment from 'moment';
import { Link, type useNavigate } from 'react-router-dom';

import {
  EDIT_SENDOUTS_PATH,
  U_ACTION,
  U_CLIENT_RECORDS_COST,
  U_COUNT_CLIENT_RECORDS,
  U_COUNT_CLIENT_REPLIES,
  U_INTEGRATIONS,
  U_KEY,
  U_NAME,
  U_RECEIVERS_COUNT,
  U_SENDOUT_CONVERSION,
  U_SENT_COUNT,
  U_START_DATE,
  U_STATUS,
} from '@app/constants';
import { SendoutStatuses } from '@app/constants/sendoutStatuses';
import type { NSendoutsApi } from '@app/services/rtkQuery/Sendouts/types';
import { SocialIntegrationIcon } from '@app/shared/ui/SocialIntegrationIcon';
import { divideAmount } from '@app/utils';

import { getStatusText } from '@pages/Sendouts/utils/getStatusText';

import type { IconProp } from '@fortawesome/fontawesome-svg-core';

const { Text, Paragraph } = Typography;

export const adaptDataToTable = (
  data: NSendoutsApi.GetSendoutsResponse['Data'],
  isAccessToManageSendoutInSendouts: boolean,
  startSendout: (id: number) => void,
  stopSendout: (id: number) => void,
  navigate: ReturnType<typeof useNavigate>
) => {
  return data.map((sendout) => {
    const {
      Id,
      TimeStart,
      Status,
      Message,
      ErrorMessage,
      Name,
      CountReceivers,
      CountSentSuccess,
      CountSentFailure,
      Channel,
      Integration,
      IsAttachments,
      CountClientReplies,
      CountClientRecords,
      SendoutConversion,
      ClientRecordsCost,
    } = sendout;
    const { RUNNING, PAUSED } = SendoutStatuses;
    const isStopButton = Status === RUNNING || Status === PAUSED;

    const getProgressPercent = (
      CountSentSuccess: number,
      CountSentFailure: number,
      CountReceivers: number
    ) => {
      if (!CountReceivers) {
        return null;
      }

      const total = CountSentSuccess + CountSentFailure;
      return total > 0
        ? `${Math.round((total / CountReceivers) * 100)}%`
        : null;
    };

    const showEditButton = Status === SendoutStatuses.STOPPED;

    return {
      [U_KEY]: String(Id),
      [U_NAME]: (
        <div
          style={{
            minWidth: '180px',
          }}
        >
          <Link to={`/sendouts/view/?id=${Id}`} data-testid="sendout-name">
            {Name}
          </Link>
          <br />
          <div style={{ position: 'relative' }}>
            {IsAttachments && (
              <FontAwesomeIcon
                icon={'fa fa-paperclip' as IconProp}
                size="sm"
                className="attachments-icon"
              />
            )}
            <Paragraph
              ellipsis={{ rows: 3 }}
              style={{ maxWidth: '300px' }}
              data-testid="sendout-text"
            >
              {Message}
            </Paragraph>
          </div>
        </div>
      ),
      [U_INTEGRATIONS]: (
        <Flex style={{ minWidth: '180px' }} align="center" gap={12}>
          <SocialIntegrationIcon type={Integration} size="lg" />
          <div>
            <span data-testid="channel-name">{Channel}</span> <br />
            <span data-testid="integration-name">{Integration}</span>
          </div>
        </Flex>
      ),
      [U_START_DATE]: (
        <div data-testid="start-date-time">
          {TimeStart ? moment(TimeStart).format('DD.MM.YYYY HH:mm') : ''}
        </div>
      ),
      [U_RECEIVERS_COUNT]: (
        <div data-testid="count-recipients">{CountReceivers}</div>
      ),
      [U_SENT_COUNT]: (
        <div>
          <div>
            <Text
              style={{ color: 'rgb(45, 165, 64)' }}
              data-testid="count-sent"
            >
              {CountSentSuccess}
            </Text>
            <Text style={{ color: 'rgb(255, 165, 0)' }}>
              {CountSentFailure > 0 && ` | ${CountSentFailure}`}
            </Text>
          </div>
        </div>
      ),
      [U_COUNT_CLIENT_REPLIES]: (
        <span data-testid="count-replied">{CountClientReplies}</span>
      ),
      [U_COUNT_CLIENT_RECORDS]: (
        <span data-testid="count-booked">{CountClientRecords}</span>
      ),
      [U_SENDOUT_CONVERSION]: (
        <span data-testid="percent-conversion">{SendoutConversion}</span>
      ),
      [U_CLIENT_RECORDS_COST]: (
        <span data-testid="amount-operations">
          {divideAmount(ClientRecordsCost)}
        </span>
      ),
      [U_STATUS]: (
        <div>
          <div data-testid="status-sendout">
            {getStatusText(Status, ErrorMessage)}
          </div>
          {Status === SendoutStatuses.RUNNING ||
          Status === SendoutStatuses.STOPPED ? (
            <div data-testid="percent-sendout">
              {getProgressPercent(
                CountSentSuccess,
                CountSentFailure,
                CountReceivers
              )}
            </div>
          ) : null}
        </div>
      ),
      [U_ACTION]: (
        <Flex gap={10}>
          {isAccessToManageSendoutInSendouts &&
            (Status === SendoutStatuses.CREATED ||
              Status === SendoutStatuses.STOPPED) && (
              <Tooltip title="Запустить рассылку">
                <Button
                  size="small"
                  onClick={() => {
                    startSendout(Id);
                  }}
                  data-testid="run-sendout"
                >
                  <FontAwesomeIcon icon={'fa-solid fa-play' as IconProp} />
                </Button>
              </Tooltip>
            )}
          {isAccessToManageSendoutInSendouts && isStopButton && (
            <Tooltip title="Остановить рассылку">
              <Button
                size="small"
                onClick={() => {
                  stopSendout(Id);
                }}
                data-testid="stop-sendout"
              >
                <FontAwesomeIcon icon={'fa-solid fa-stop' as IconProp} />
              </Button>
            </Tooltip>
          )}
          <Tooltip placement="bottomRight" title="Посмотреть детали рассылки">
            <Button
              size="small"
              onClick={() => {
                navigate(`/sendouts/view/?id=${Id}`);
              }}
              data-testid="view-details"
            >
              <FontAwesomeIcon icon={'fa-solid fa-eye' as IconProp} />
            </Button>
          </Tooltip>
          {showEditButton && (
            <Tooltip title="Редактировать рассылку">
              <Button
                size="small"
                icon={<FontAwesomeIcon icon={faEdit} />}
                onClick={() => {
                  navigate(`${EDIT_SENDOUTS_PATH}/${Id}`);
                }}
                data-testid="edit-sendout"
              />
            </Tooltip>
          )}
        </Flex>
      ),
    };
  });
};
